/**
 * @description 规范化官网内容搜索词，统一兼容空白与全半角输入。
 * @param {unknown} value - 原始搜索词
 * @returns {string} 规范化后的搜索词
 */
export function normalizeServiceSearchKeyword(value) {
  return String(value ?? '')
    .normalize('NFKC')
    .replace(/\s+/gu, ' ')
    .trim()
}

/**
 * @description 按后端相同规则拆分官网内容搜索词并去重。
 * @param {unknown} value - 原始搜索词
 * @returns {Array<string>} 有序且去重的搜索词数组
 */
export function tokenizeServiceSearchKeyword(value) {
  const seen = new Set()

  return normalizeServiceSearchKeyword(value)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .filter((token) => {
      const deduplicationKey = token.toLowerCase()
      if (seen.has(deduplicationKey)) return false
      seen.add(deduplicationKey)
      return true
    })
}

/**
 * @description 安全转义文本后逐词高亮，避免依赖原始空白数量进行整句匹配。
 * @param {unknown} value - 待展示文本
 * @param {unknown} keyword - 原始搜索词
 * @param {string} highlightColor - 高亮颜色
 * @returns {string} 可交给 v-safe-html 的高亮 HTML
 */
export function highlightServiceSearchText(value, keyword, highlightColor) {
  const text = String(value ?? '')
  const tokens = tokenizeServiceSearchKeyword(keyword).sort(
    (left, right) => right.length - left.length
  )

  if (tokens.length === 0) return escapeHtml(text)

  const pattern = tokens.map(escapeRegExp).join('|')
  const matcher = new RegExp(pattern, 'giu')
  let cursor = 0
  let html = ''

  for (const match of text.matchAll(matcher)) {
    const matchIndex = match.index ?? cursor
    html += escapeHtml(text.slice(cursor, matchIndex))
    html += `<span style="color: ${highlightColor};">${escapeHtml(match[0])}</span>`
    cursor = matchIndex + match[0].length
  }

  return html + escapeHtml(text.slice(cursor))
}

/**
 * @description 将内容筛选数组去重、稳定排序并压缩为逗号分隔查询值。
 * @param {unknown} value - 单值或数组筛选值
 * @returns {string|undefined} 压缩后的查询值
 */
export function serializeServiceContentFilterValues(value) {
  const values = Array.isArray(value) ? value : value == null || value === '' ? [] : [value]
  const normalized = Array.from(
    new Set(values.map((item) => String(item ?? '').trim()).filter(Boolean))
  ).sort((left, right) => left.localeCompare(right, 'en', { numeric: true }))

  return normalized.length > 0 ? normalized.join(',') : undefined
}

function escapeHtml(value) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  }

  return String(value).replace(/[&<>"']/g, (character) => map[character])
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
